import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiCommon } from "@/service/apiCommon";

export interface Tenant {
  IdTenant: number;
  Name: string;
  Cnpj: string;
  Email: string;
  LogoUrl: string | null;
  Domain?: string | null;
  CheckoutDomain?: string | null;
  AccentColor?: string;
  SecondaryColor?: string;
  BackgroundColor?: string;
  Status: boolean;
}

export interface TenantState {
  loginTenant: Tenant | null;
  loadingByDomain: boolean;
  errorByDomain: string | null;
  tokenizePublicToken: string | null;
  tokenizeIsProduction: boolean;
}

const initialState: TenantState = {
  loginTenant: null,
  loadingByDomain: false,
  errorByDomain: null,
  tokenizePublicToken: null,
  tokenizeIsProduction: false,
};

function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

export function resolveCheckoutDomain(hostname: string): string | null {
  const host = hostname.trim().toLowerCase();
  const envDomain = (
    import.meta.env.VITE_CHECKOUT_DOMAIN || import.meta.env.VITE_TENANT_DOMAIN
  )?.trim().toLowerCase();

  if (isLocalHostname(host)) {
    return envDomain || null;
  }

  return host;
}

export const fetchTenantByDomain = createAsyncThunk(
  "tenant/fetchTenantByDomain",
  async (hostname: string, { rejectWithValue }) => {
    const domain = resolveCheckoutDomain(hostname);

    if (!domain) {
      return rejectWithValue("Barbearia não encontrada");
    }

    try {
      const response = await apiCommon.get(
        `no-auth/tenant/by-checkout-domain?domain=${encodeURIComponent(domain)}`
      );
      const { data }: { data: Tenant } = response;
      return data;
    } catch (error: unknown) {
      const response = (
        error as { response?: { status?: number; data?: { message?: string } } }
      )?.response;

      if (response?.status === 404) {
        return rejectWithValue("Barbearia não encontrada");
      }

      return rejectWithValue(
        response?.data?.message || "Erro ao buscar dados da barbearia"
      );
    }
  }
);

const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    clearLoginTenant: (state) => {
      state.loginTenant = null;
      state.errorByDomain = null;
      state.tokenizePublicToken = null;
    },
    setTokenizeConfig: (
      state,
      action: PayloadAction<{ publicToken: string; isProduction: boolean }>
    ) => {
      state.tokenizePublicToken = action.payload.publicToken;
      state.tokenizeIsProduction = action.payload.isProduction;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenantByDomain.pending, (state) => {
        state.loadingByDomain = true;
        state.errorByDomain = null;
      })
      .addCase(
        fetchTenantByDomain.fulfilled,
        (state, action: PayloadAction<Tenant>) => {
          state.loadingByDomain = false;
          state.loginTenant = action.payload;
          state.errorByDomain = null;
          state.tokenizePublicToken = null;
        }
      )
      .addCase(fetchTenantByDomain.rejected, (state, action) => {
        state.loadingByDomain = false;
        state.errorByDomain = action.payload as string;
        state.loginTenant = null;
        state.tokenizePublicToken = null;
      });
  },
});

export const { clearLoginTenant, setTokenizeConfig } = tenantSlice.actions;
export default tenantSlice.reducer;
